import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { PerformanceReviewInput, ReviewActionItemInput } from './types';

export const reviewKeys = {
  all: ['performance-reviews'] as const,
  lists: () => [...reviewKeys.all, 'list'] as const,
  list: (p: api.ReviewListParams) => [...reviewKeys.lists(), p] as const,
  detail: (id: string | number) => [...reviewKeys.all, 'detail', id] as const,
};

/** Truthy id that isn't the `-1` "skip" sentinel used by optional lookups. */
function isResolvable(id: string | number): boolean {
  return id !== '' && id !== -1 && id !== '-1' && id != null;
}

export function useReviews(params: api.ReviewListParams = {}) {
  return useQuery({
    queryKey: reviewKeys.list(params),
    queryFn: () => api.listReviews(params),
    placeholderData: keepPreviousData,
  });
}

export function useReview(id: string | number) {
  return useQuery({
    queryKey: reviewKeys.detail(id),
    queryFn: () => api.getReview(id),
    // Resolves by public_id (string, from the URL) or integer pk; the backend
    // accepts either (ADR-041).
    enabled: isResolvable(id),
  });
}

export function useCreateReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PerformanceReviewInput) => api.createReview(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reviewKeys.lists() });
      toast.success('Review saved');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateReview(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<PerformanceReviewInput>) => api.updateReview(id, input),
    onSuccess: (review) => {
      qc.invalidateQueries({ queryKey: reviewKeys.lists() });
      qc.setQueryData(reviewKeys.detail(id), review);
      toast.success('Review updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.deleteReview(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reviewKeys.lists() });
      toast.success('Review deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Action items — mutations refresh the parent review (nested list) ------

export function useCreateReviewActionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReviewActionItemInput) => api.createReviewActionItem(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: reviewKeys.all }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useUpdateReviewActionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<ReviewActionItemInput> }) =>
      api.updateReviewActionItem(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: reviewKeys.all }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeleteReviewActionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteReviewActionItem(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: reviewKeys.all }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}
